import { Badge, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPause,
  faArrowRight,
  faScissors,
  faArrowUpRightFromSquare
} from '@fortawesome/free-solid-svg-icons';

/**
 * The desfășurător: how a job's length got broken up on the way through.
 *
 * Project Bobin State gives one row per token, which answers "where is this
 * drum". It cannot answer the question underneath — how did twenty thousand
 * metres become four drums of five thousand, and at which phase. That is a
 * shape, not a list, so here the phases are columns and each row is one
 * lineage: a split is four chips under one chip, and you can see it without
 * reading a single number.
 */

const fmt = n => Math.round(Number(n) || 0).toLocaleString();

const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-stranding',
  conductor_isolation: 'Insulation',
  rope: 'Rope',
  interior_sheat: 'Inner sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Outer sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};

const STATUS_BG = {
  DONE: 'success',
  FINISHED: 'success',
  COMPLETED: 'success',
  RUNNING: 'primary',
  IN_PROGRESS: 'primary',
  PENDING: 'secondary'
};

const TokenChip = ({ token }) => {
  const bg = STATUS_BG[String(token.status || '').toUpperCase()] || 'secondary';

  const tip = (
    <Tooltip>
      <div className="text-start">
        <div className="font-monospace">{token.tokenId}</div>
        <div>{token.status || 'unknown'}</div>
        <div>
          planned {fmt(token.plannedLength)} m · produced{' '}
          {fmt(token.producedLength)} m
        </div>
        <div>
          free {fmt(token.free)} m
          {token.reservedLength > 0 && ` · reserved ${fmt(token.reservedLength)} m`}
        </div>
        {token.drumMaterial && (
          <div>
            {token.drumMaterial}
            {token.drumDiameter ? ` Ø${token.drumDiameter}` : ''}
          </div>
        )}
        {token.coilCount > 0 && (
          <div>
            {token.coilCount} × {fmt(token.coilLength)} m coils
          </div>
        )}
        {token.splitInto > 1 && <div>split into {token.splitInto}</div>}
        {token.shared && <div>shared take-up with another product</div>}
      </div>
    </Tooltip>
  );

  return (
    <OverlayTrigger overlay={tip} placement="top">
      <div
        className={`d-inline-flex align-items-center gap-1 border rounded px-2 py-1 mb-1 me-1 bg-body-emphasis`}
        style={{ cursor: 'default' }}
      >
        {token.color && (
          <span
            className="rounded-circle border d-inline-block"
            style={{
              width: 8,
              height: 8,
              backgroundColor: String(token.color).toLowerCase().includes('green')
                ? '#c9d227'
                : String(token.color).toLowerCase()
            }}
          />
        )}
        <span className="fw-semibold fs-10">{fmt(token.free)} m</span>
        <Badge bg={bg} variant="phoenix" className="fs-10">
          {token.status || '—'}
        </Badge>
        {token.destination === 'ORDER' && (
          <Badge bg="info" variant="phoenix" className="fs-10">
            order
          </Badge>
        )}
        {token.shared && (
          <Badge bg="warning" variant="phoenix" className="fs-10">
            shared
          </Badge>
        )}
        {token.splitInto > 1 && (
          <span className="text-body-tertiary fs-10">
            <FontAwesomeIcon icon={faScissors} /> {token.splitInto}
          </span>
        )}
        <FontAwesomeIcon
          icon={token.plannedOnward ? faArrowRight : faPause}
          className={
            token.plannedOnward ? 'text-body-tertiary fs-10' : 'text-warning fs-10'
          }
          title={token.plannedOnward ? 'planned onward' : 'waiting for a decision'}
        />
      </div>
    </OverlayTrigger>
  );
};

const TokenMapTable = ({ job, className = '' }) => {
  if (!job) return null;

  const { route = [], chains = [], totals = {}, cores } = job;

  return (
    <div className={className}>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <h4 className="text-1100 fs-8 mb-0">Where the length went</h4>
        {/* The job code is the way into the product. Opening it in a new tab
            rather than navigating keeps the planner's place — this screen is
            something you consult mid-decision, not somewhere you leave. */}
        <a
          href={`/apps/project-management/project-details/${job.jobId}`}
          target="_blank"
          rel="noreferrer"
          className="text-decoration-none"
          title="Open this product in a new tab"
        >
          <span className="badge bg-primary-100 text-primary-600 border border-primary rounded px-2 py-1 fw-bold">
            {job.jobCode}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-2 fs-10" />
          </span>
        </a>
        <span className="text-700 fs-9">
          {fmt(totals.deliverable)} m of cable
          {totals.onDrums !== totals.deliverable && (
            <span className="text-body-tertiary">
              {' '}
              ({fmt(totals.onDrums)} m on {totals.tokens} drums —{' '}
              {job.strandCount} cores make one cable)
            </span>
          )}
        </span>
        {totals.waiting > 0 && (
          <Badge bg="warning" variant="phoenix">
            {totals.waiting} waiting for a next phase
          </Badge>
        )}
      </div>

      {cores && !cores.complete && (
        <div className="text-danger fs-9 mb-2">
          Cannot be stranded yet — a {job.strandCount}-core cable needs one of
          each colour, and the floor has{' '}
          {cores.cores.map(c => `${c.color} ${c.count}`).join(', ') || 'nothing'}.
        </div>
      )}

      <div className="table-responsive">
        <Table size="sm" bordered className="fs-9 mb-0" style={{ minWidth: 640 }}>
          <thead>
            <tr>
              {route.map(phase => (
                <th key={phase} className="text-nowrap">
                  {PHASE_LABEL[phase] || phase}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {chains.map(chain => (
              <tr key={chain.chainId}>
                {route.map(phase => {
                  const tokens = chain.byPhase?.[phase] || [];
                  return (
                    <td key={phase} className="align-top">
                      {tokens.length ? (
                        tokens.map(t => <TokenChip key={t.tokenId} token={t} />)
                      ) : (
                        <span className="text-body-tertiary">·</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!chains.length && (
              <tr>
                <td colSpan={route.length} className="text-body-tertiary">
                  Nothing launched yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {chains.some(c => c.splits.length) && (
        <div className="text-body-tertiary fs-10 mt-2">
          <FontAwesomeIcon icon={faScissors} className="me-1" />
          {chains
            .flatMap(c => c.splits)
            .map(
              s =>
                `${fmt(s.length)} m split into ${s.into} at ${
                  PHASE_LABEL[s.phase] || s.phase
                }`
            )
            .join(' · ')}
        </div>
      )}
    </div>
  );
};

export default TokenMapTable;
